import SeoFooter from "../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Building2, Users, Clock, Shield, CreditCard, Calendar,
  Phone, Mail, CheckCircle2, Briefcase, Globe, Star,
  FileText, Plane, Car, ArrowRight, Lock, UserCheck
} from "lucide-react";
import ContactPopup from "../components/ContactPopup";
import PageTransition from "../components/PageTransition";

const CorporateTravelPage = () => {
  const whyTrustUs = [
    {
      icon: Lock,
      title: "Confidentiality",
      description: "Our chauffeurs sign non-disclosure agreements to ensure your business conversations remain private."
    },
    {
      icon: CreditCard,
      title: "Corporate Account Management",
      description: "Streamlined invoicing and priority booking for frequent travelers."
    },
    {
      icon: Clock,
      title: "Punctuality Guarantee",
      description: "We arrive 15 minutes early to every corporate booking—no exceptions."
    },
    {
      icon: Users,
      title: "Event Coordination",
      description: "Planning a conference or a roadshow? We can manage a fleet of vehicles to move your entire team seamlessly."
    }
  ];

  const services = [
    {
      title: "Executive Airport Transfers",
      description: "Seamless pickup and drop-off at all major Australian airports with flight tracking.",
      icon: Plane,
      city: "Corporate Airport Transfers Brisbane, Sydney, Melbourne"
    },
    {
      title: "Executive Transport",
      description: "Premium vehicles and professional chauffeurs for VIP clients and executives.",
      icon: Briefcase,
      city: "Executive Limo Hire Gold Coast, Cairns, Perth"
    },
    {
      title: "Conference & Event Transport",
      description: "Coordinated transport solutions for conferences, meetings, and corporate events.",
      icon: Users,
      city: "Corporate Event Transport Australia"
    },
    {
      title: "Roadshows & Multi-City",
      description: "Seamless logistics for multi-destination business trips across Australia.",
      icon: Globe,
      city: "Business Travel Australia Wide"
    }
  ];

  const testimonials = [
    {
      quote: "Limo Near Me has transformed our corporate travel. The reliability and professionalism are unmatched. Their confidentiality and punctuality are exactly what we need.",
      author: "Sarah Chen",
      role: "Travel Manager, TechCorp Australia"
    },
    {
      quote: "The dedicated account manager and flexible billing make managing our executive travel effortless. They're always 15 minutes early.",
      author: "Michael Roberts",
      role: "CFO, Global Finance Group"
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Corporate Travel & Executive Transportation - Limo Near Me",
    "description": "Executive transportation and corporate chauffeur services across Australia. Confidential, punctual, and professional transport for business executives, VIP guests, and corporate teams in Brisbane, Sydney, Melbourne, Gold Coast.",
    "provider": {
      "@type": "Organization",
      "name": "Limo Near Me",
      "telephone": "+61481437772",
      "email": "info@limonearme.com.au"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Australia"
    },
    "serviceType": "Corporate Ground Transportation"
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Corporate Limo Hire | Executive Transportation Australia | Limo Near Me</title>
          <meta name="description" content="Executive transportation: Your office on the move. Professional corporate chauffeur services in Brisbane, Sydney, Melbourne, Gold Coast. Confidential, punctual, NDA-signed chauffeurs. Open a corporate account today." />
          <meta name="keywords" content="corporate limo hire Brisbane, executive transport Sydney, corporate chauffeur Melbourne, business travel Gold Coast, corporate car service Australia, executive limo hire, corporate account transport" />
          <link rel="canonical" href="https://www.limonearme.com.au/corporate-travel" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Our Fleet</Link>
                <Link to="/services/airport-transfers" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Airport Transfers</Link>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">About Us</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                  <Building2 className="h-4 w-4" />
                  <span className="text-sm">Corporate Solutions</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  Executive Transportation: Your Office on the Move
                </h1>
                <p className="text-xl text-gray-300 mb-8">
                  Professional chauffeur services tailored for the modern business world.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/register">
                    <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-8">
                      Open a Corporate Account
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/book">
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8">
                      <Car className="mr-2 h-4 w-4" />
                      Book an Executive Vehicle
                    </Button>
                  </Link>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="hidden md:block"
              >
                <img 
                  src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=600&h=400&fit=crop" 
                  alt="Corporate travel executive" 
                  className="rounded-2xl shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Experience Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <h2 className="text-3xl font-bold text-foreground mb-6">The Experience</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                In business, first impressions are everything. LimoNearMe provides a discreet, punctual, and sophisticated transport solution for executives, VIP guests, and corporate teams. Our vehicles offer a quiet, climate-controlled environment, allowing you to take calls or prepare for meetings in total privacy.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Why Corporate Clients Trust Us */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Why Corporate Clients Trust Us</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We understand the unique demands of business travel and deliver accordingly.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyTrustUs.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">Our Corporate Services</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Comprehensive executive transportation solutions for every business need.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 p-8 rounded-xl hover:shadow-md transition-shadow flex gap-6"
                >
                  <div className="w-14 h-14 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                    <p className="text-muted-foreground mb-2">{service.description}</p>
                    <p className="text-xs text-primary">{service.city}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Trusted by Leading Businesses</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/10 p-8 rounded-xl"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-lg mb-6 text-gray-300 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.author}</p>
                    <p className="text-sm text-gray-400">{testimonial.role}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Getting Started is Easy</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Set up your corporate account in minutes and start booking right away.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: "1", title: "Open Account", description: "Register your corporate account online or contact our dedicated sales team." },
                { step: "2", title: "Customize", description: "Set up billing preferences, add authorized bookers, and define travel policies." },
                { step: "3", title: "Book & Go", description: "Start booking through our portal with priority service and 15-minute early arrival guarantee." }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-gray-900 text-white flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Streamline Your Corporate Travel?</h2>
            <p className="text-primary-foreground/80 mb-8 text-lg">
              Join hundreds of businesses across Australia that trust Limo Near Me for their executive transportation needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" variant="secondary" className="rounded-full px-8">
                  Open a Corporate Account
                </Button>
              </Link>
              <Link to="/book">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-white/30 text-white hover:bg-white/10">
                  <Car className="mr-2 h-4 w-4" />
                  Book an Executive Vehicle
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default CorporateTravelPage;
